import { EventSponsor } from "@/types/EventSponsor";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import { api } from "@/lib/api/client";
import { apiPost, apiGet, apiDelete, apiPut } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/event-sponsors/";

const SPONSOR_FIELDS =
  "sponsorId,eventId,name,logoUrl,linkUrl,tier,tierId,description,sortIndex,createdAt," +
  "contactName,contactEmail,tags,sourceContactId," +
  // Wave A public attachments (BE ALLOWED_FIELDS mirror).
  "attachments," +
  // Wave D display toggles (bake reads them from the legacy GET).
  "hiddenFields," +
  // publicOnWebsite-gated custom values — speaker twin (EventSpeakerClient).
  "additionalFields";

/** Admin hub list projection — the ONLY surface that carries custom values. */
const SPONSOR_LIST_FIELDS = SPONSOR_FIELDS + ",additionalFields";

export async function createEventSponsor(
  eventId: string,
  sponsor: EventSponsor,
  /**
   * Art. 50 Abs. 2 KI-VO (J6): the AI lane behind this create — the JSON
   * transfer of a file from an AI-marked (or pre-wave) event sends
   * `template_import`. The body IS the entity, so it travels as
   * `?aiContentTool=`; the server stamps the event's guest-content mark.
   */
  aiContentTool?: string,
): Promise<string> {
  return await apiPost<string>(withAiContentTool(`${BASE_PATH}${eventId}`, aiContentTool), sponsor, {
    "Cache-Control": "no-store",
  });
}

/**
 * All sponsors of an event. THROWS on failure (see EventSpeakerClient) so a
 * future binding refresher never mistakes an outage for an empty list.
 */
export async function getEventSponsors(
  eventId: string,
): Promise<EventSponsor[]> {
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent(SPONSOR_FIELDS)}`;
  return await apiGet<EventSponsor[]>(url, { "Cache-Control": "no-store" });
}

/**
 * ONE sponsor including `additionalFields` (LIST projection). Twin of
 * getEventSpeakerDetail — the builder popup validates required custom
 * fields, but the legacy list projection omits them by design, which made
 * Save dead and then overwrote the stored value on the retry.
 */
export async function getEventSponsorDetail(
  eventId: string,
  sponsorId: string,
): Promise<EventSponsor> {
  const url = `${BASE_PATH}${eventId}/${sponsorId}/detail?fields=${encodeURIComponent(SPONSOR_LIST_FIELDS)}`;
  return await apiGet<EventSponsor>(url, { "Cache-Control": "no-store" });
}

export async function updateEventSponsor(
  eventId: string,
  sponsorId: string,
  sponsor: Partial<EventSponsor>,
): Promise<void> {
  await apiPut<string>(`${BASE_PATH}${eventId}/${sponsorId}`, sponsor, {
    "Cache-Control": "no-store",
  });
}

/** Wave L sponsor↔guests link (guest-side hex marker). 409
 *  guestAlreadyLinkedToSponsor when the guest belongs to another sponsor. */
export async function linkSponsorGuest(
  eventId: string,
  sponsorId: string,
  guestId: string,
): Promise<void> {
  await apiPost<string>(`${BASE_PATH}${eventId}/${sponsorId}/link-guest`, {
    guestId,
  }, { "Cache-Control": "no-store" });
}

export async function unlinkSponsorGuest(
  eventId: string,
  sponsorId: string,
  guestId: string,
): Promise<void> {
  await apiPost<string>(`${BASE_PATH}${eventId}/${sponsorId}/unlink-guest`, {
    guestId,
  }, { "Cache-Control": "no-store" });
}

export async function deleteEventSponsor(
  eventId: string,
  sponsorId: string,
): Promise<void> {
  await apiDelete<string>(`${BASE_PATH}${eventId}/${sponsorId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Wave A pinned contract — server-side paginated/filtered sponsor list.
 * `POST /api/v1/event-sponsors/{eventId}/list`; pageSize capped at 100 by the
 * resource (`pageSizeExceeded`), default sort `sortIndex asc, name asc`.
 * Returns sponsors INCLUDING `additionalFields` (admin-only surface).
 */
export async function listEventSponsorsPaginated(
  eventId: string,
  params: {
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  },
): Promise<EventSponsor[]> {
  return await apiPost<EventSponsor[]>(
    `${BASE_PATH}${eventId}/list`,
    { fields: SPONSOR_LIST_FIELDS, ...params },
    { "Cache-Control": "no-store" },
    true,
  );
}

/** Wave A pinned contract — count twin of the list endpoint. */
export async function countEventSponsors(
  eventId: string,
  params: {
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
  },
): Promise<number> {
  return await apiPost<number>(
    `${BASE_PATH}${eventId}/count`,
    params,
    { "Cache-Control": "no-store" },
    true,
  );
}

/**
 * Wave A pinned contract — synchronous sponsor export (csv|excel), mirroring
 * the check-in-log export resource idiom. Filter/search-scoped (the contract
 * carries no row-id selection). Returns the file as a Blob.
 */
export async function exportEventSponsors(
  eventId: string,
  params: {
    format: "csv" | "excel";
    fields?: string;
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    columnLabels?: Record<string, string>;
  },
): Promise<Blob> {
  const r = await api.post(`${BASE_PATH}${eventId}/export`, params, {
    responseType: "blob",
    headers: { "Cache-Control": "no-store" },
  });
  return r.data as Blob;
}

/**
 * Import a sponsor prefilled from a CRM contact of the event's company
 * (company name → sponsor name, person → contactName/-Email). Returns the
 * new sponsor id.
 */
export async function importEventSponsor(
  eventId: string,
  contactId: string,
): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}${eventId}/import`,
    { contactId },
    { "Cache-Control": "no-store" },
  );
}
